import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Pagination, Table } from 'antd';
import { ButtonCommon, IconCommon, InputCommon, SelectCommon } from 'components/common';
import { useDebouncedCallback } from 'use-debounce';
import { useGetAllCategoriesQuery, useGetProductsQuery } from 'services';
import { ProductVersion } from 'models';
import Category from 'models/Category';
import { formatPrice, getFilePath } from 'utils';
import { QueryParams } from 'types';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { icon_ton, icon_vlxd, sat_vuong } from 'utils/importFile';

interface ProductSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (products: ProductVersion[]) => void;
  selectedProducts?: ProductVersion[];
  selectionType?: 'basic' | 'advanced';
}

const ProductSearchModal: React.FC<ProductSearchModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedProducts = [],
}) => {
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedMap, setSelectedMap] = useState<Record<string, ProductVersion>>({});

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setDebouncedKeyword(value);
    setPage(1);
  }, 300);

  useEffect(() => {
    if (isOpen) {
      const initialMap = selectedProducts.reduce(
        (acc, product) => {
          if (product?.id) acc[product.id] = product;
          return acc;
        },
        {} as Record<string, ProductVersion>,
      );
      setSelectedMap(initialMap);
      setSelectedRowKeys(selectedProducts.map((product) => product.id));
    }
  }, [isOpen, selectedProducts]);

  const { data: categoriesData, isLoading: isLoadingCategories } = useGetAllCategoriesQuery({
    query: { size: 'unlimited', filter: 'isActive:eq:true', include: 'parent' },
  });
  const baseFilter = 'isActive:eq:true&&status.code:eq:pr-active';
  const queryFilter = useMemo(() => {
    const conditions = [baseFilter];
    if (categoryId) conditions.push(`categoryId:eq:${categoryId}`);
    if (minPrice) conditions.push(`salePrice:gte:${minPrice}`);
    if (maxPrice) conditions.push(`salePrice:lte:${maxPrice}`);
    return conditions.join('&&');
  }, [baseFilter, categoryId, minPrice, maxPrice]);

  const {
    data: productsData,
    isLoading: isLoadingProducts,
    isFetching,
  } = useGetProductsQuery({
    query: {
      size: pageSize,
      page,
      include: 'product|category|brand|baseUnit|currencyUnit|productImages.image|product.productSuppliers.supplier',
      filter: queryFilter,
      sort: 'updatedAt:desc',
      searchKey: 'name|color|dimension|sizeSpec|product.code|product.barcode',
      searchValue: debouncedKeyword,
    } as QueryParams,
  });

  const rows = useMemo(() => productsData?.data?.rows || [], [productsData]);

  const filteredRows = useMemo(() => rows, [rows]);

  const totalCount = productsData?.data?.count || 0;

  const categories = useMemo(() => categoriesData?.data?.rows || [], [categoriesData]);

  const getCategoryIcon = useCallback((name: string) => {
    const lower = (name || '').toLowerCase();
    if (lower.includes('tôn')) return <IconCommon src={icon_ton} className='mr-2' />;
    if (lower.includes('thép')) return <IconCommon src={sat_vuong} className='mr-2' />;
    if (lower.includes('sắt')) return <IconCommon src={sat_vuong} className='mr-2' />;
    return <IconCommon src={icon_vlxd} className='mr-2' />;
  }, []);

  const categoryOptions = useMemo(() => {
    const sortedByOrder = [...(categories as Category[])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const ton = sortedByOrder.filter((c) => c.name.toLowerCase().includes('tôn'));
    const thep = sortedByOrder.filter((c) => c.name.toLowerCase().includes('thép'));
    const sat = sortedByOrder.filter((c) => c.name.toLowerCase().includes('sắt'));
    const others = sortedByOrder.filter(
      (c) =>
        !c.name.toLowerCase().includes('tôn') &&
        !c.name.toLowerCase().includes('thép') &&
        !c.name.toLowerCase().includes('sắt'),
    );
    const sorted = [...ton, ...thep, ...sat, ...others];

    return [
      {
        label: (
          <div className='flex items-center'>
            <IconCommon src={icon_vlxd} className='mr-2' />
            <span>Tất cả danh mục</span>
          </div>
        ),
        value: '',
        searchText: 'Tất cả danh mục',
      },
      ...sorted.map((category) => {
        const level = category.level ?? 0;
        const indent = level > 0 ? `${'—'.repeat(level)} ` : '';
        const labelText = `${indent}${category.name}`;
        return {
          label: (
            <div className='flex items-center'>
              {getCategoryIcon(category.name)}
              <span>{labelText}</span>
            </div>
          ),
          value: category.id,
          searchText: labelText,
        };
      }),
    ];
  }, [categories, getCategoryIcon]);

  const getImageUrl = (record: ProductVersion) => {
    const filePath = record.productImages?.[0]?.image?.filePath;
    if (!filePath) return undefined;
    return `${getFilePath()}/${filePath}`;
  };

  const getDisplayProductName = (record: ProductVersion) =>
    (record.name || '') +
    (record.color ? ` - ${record.color}` : '') +
    (record.dimension ? ` - ${record.dimension}` : '') +
    (record.sizeSpec ? ` - ${record.sizeSpec}` : '');

  const handleSelectAll = () => {
    const visibleIds = filteredRows.map((row: ProductVersion) => row.id);
    setSelectedRowKeys((prev) => Array.from(new Set([...prev, ...visibleIds])));
    setSelectedMap((prev) => {
      const next = { ...prev };
      filteredRows.forEach((row) => {
        if (row?.id) next[row.id] = row;
      });
      return next;
    });
  };

  const handleDeselectAll = () => {
    const visibleIds = new Set(filteredRows.map((row: ProductVersion) => row.id));
    setSelectedRowKeys((prev) => prev.filter((key) => !visibleIds.has(key)));
    setSelectedMap((prev) => {
      const next = { ...prev };
      filteredRows.forEach((row) => {
        if (row?.id) delete next[row.id];
      });
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedProductsList = Object.values(selectedMap);
    onConfirm([...selectedProducts, ...selectedProductsList]);
    onClose();
  };

  const columns = [
    {
      title: 'Ảnh',
      dataIndex: 'image',
      width: 70,
      render: (_: string, record: ProductVersion) => (
        <div className='w-12 h-12 rounded-lg bg-[var(--textBlack12)] overflow-hidden flex items-center justify-center'>
          {getImageUrl(record) ? (
            <img src={getImageUrl(record)} alt={record.name || 'product'} className='w-full h-full object-cover' />
          ) : (
            <span className='text-[10px] text-gray-400'>N/A</span>
          )}
        </div>
      ),
    },
    {
      title: 'Sản phẩm',
      dataIndex: 'name',
      render: (_: string, record: ProductVersion) => (
        <div className='flex flex-col'>
          <span className='font-medium text-black'>{getDisplayProductName(record) || '---'}</span>
          <span className='text-xs text-gray-500'>{record.product?.code || ''}</span>
        </div>
      ),
    },
    {
      title: 'Danh mục',
      dataIndex: 'category',
      render: (_: string, record: ProductVersion) => record.category?.name || '---',
    },
    {
      title: 'Thương hiệu',
      dataIndex: 'brand',
      render: (_: string, record: ProductVersion) => record.brand?.name || '---',
    },
    {
      title: 'Đơn giá',
      dataIndex: 'salePrice',
      align: 'right' as const,
      render: (_: string, record: ProductVersion) => formatPrice(Number(record.salePrice || record.costPrice || 0)),
    },
    {
      title: 'Tồn',
      dataIndex: 'quantity',
      align: 'right' as const,
      render: (_: string, record: ProductVersion) => record.product?.quantity ?? record.quantity ?? 0,
    },
  ];

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={1200}
      centered
      className='modal-none-padding'
      maskClosable={false}
      style={{ zIndex: 9999 }}
    >
      <div className='flex h-[calc(100vh-120px)] max-h-[calc(100vh-120px)] flex-col bg-white rounded-lg overflow-hidden'>
        <div className='p-4 border-b border-[var(--textBlack12)] shrink-0 bg-white'>
          <h2 className='text-xl font-bold text-black'>TÌM KIẾM NÂNG CAO</h2>
          <p className='text-sm text-gray-500'>Lọc sản phẩm theo nhiều tiêu chí</p>
        </div>

        <div className='px-4 py-3 space-y-3 border-b border-[var(--textBlack12)] shrink-0 bg-white'>
          <div className='flex items-center gap-2 w-full border border-gray-200 rounded-md px-3 py-2 bg-white hover:border-blue-400 transition-colors focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'>
            <MagnifyingGlass size={18} className='text-gray-400' />
            <InputCommon
              placeholder='Tìm kiếm theo tên, mã, barcode...'
              value={keyword}
              onChange={(e) => {
                const value = e.target.value;
                setKeyword(value);
                debouncedSearch(value);
              }}
              // Loại bỏ style cũ để input hòa nhập vào container cha
              className='!bg-transparent !border-none !h-[24px] !p-0 flex-1 outline-none text-sm'
            />
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
            <div className='w-full'>
              <SelectCommon
                placeholderSelect='Tất cả danh mục'
                selectType='filterSelect'
                value={categoryId}
                options={categoryOptions}
                loading={isLoadingCategories}
                onChange={(value) => {
                  setCategoryId(value ? String(value) : '');
                  setPage(1);
                }}
                allowClear
                className='w-full'
              />
            </div>
            <InputCommon
              placeholder='Giá từ...'
              type='number'
              value={minPrice}
              onChange={(e) => {
                setMinPrice(e.target.value);
                setPage(1);
              }}
              radius='rounded'
              className='!border-gray-200 focus:!border-blue-500 hover:!border-blue-400'
            />
            <InputCommon
              placeholder='...đến giá'
              type='number'
              value={maxPrice}
              onChange={(e) => {
                setMaxPrice(e.target.value);
                setPage(1);
              }}
              radius='rounded'
              className='!border-gray-200 focus:!border-blue-500 hover:!border-blue-400'
            />
          </div>
        </div>

        <div className='px-4 py-3 min-h-0 flex-1 overflow-hidden'>
          <Table
            rowKey='id'
            dataSource={filteredRows}
            columns={columns}
            loading={isLoadingProducts || isFetching}
            pagination={false}
            scroll={{ y: 'calc(100vh - 520px)' }}
            rowSelection={{
              selectedRowKeys,
              onSelect: (record, selected) => {
                setSelectedRowKeys((prev) => (selected ? [...prev, record.id] : prev.filter((id) => id !== record.id)));

                setSelectedMap((prev) => {
                  const next = { ...prev };
                  if (selected) {
                    next[record.id] = record;
                  } else {
                    delete next[record.id];
                  }
                  return next;
                });
              },

              onSelectAll: (selected, selectedRows, changeRows) => {
                setSelectedRowKeys((prev) => {
                  const ids = changeRows.map((r) => r.id);
                  return selected ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id));
                });

                setSelectedMap((prev) => {
                  const next = { ...prev };
                  if (selected) {
                    changeRows.forEach((r) => (next[r.id] = r));
                  } else {
                    changeRows.forEach((r) => delete next[r.id]);
                  }
                  return next;
                });
              },
            }}
          />
        </div>

        <div className='px-4 py-2 border-t border-[var(--textBlack12)] bg-white shrink-0 flex justify-end'>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={totalCount}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            onChange={(nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize || pageSize);
            }}
          />
        </div>

        <div className='px-4 py-3 border-t border-[var(--textBlack12)] bg-white shrink-0 flex flex-wrap justify-between gap-3 items-center'>
          <div className='text-sm text-gray-500'>Đã chọn {selectedRowKeys.length} sản phẩm</div>
          <div className='flex gap-2'>
            <ButtonCommon color='none' size='sm' radius='rounded' onClick={handleSelectAll}>
              Chọn tất cả
            </ButtonCommon>
            <ButtonCommon color='none' size='sm' radius='rounded' onClick={handleDeselectAll}>
              Bỏ chọn tất cả
            </ButtonCommon>
            <ButtonCommon color='primary' size='sm' radius='rounded' onClick={handleConfirm}>
              Xác nhận
            </ButtonCommon>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProductSearchModal;
